import React from 'react';
import PriceInput from '@/components/inputControls/PriceInput';

interface NumberInputProps
  extends Omit<
    React.InputHTMLAttributes<HTMLInputElement>,
    'type' | 'value' | 'onChange'
  > {
  value?: number | string | null;
  onChange?: React.ChangeEventHandler<HTMLInputElement>;
  onValueChange?: (value: number | undefined) => void;
  error?: string;
  inputClassName?: string;
  allowNegative?: boolean;
  maxDecimals?: number;
  decimalSeparator?: 'comma' | 'dot';
}

const COORDINATE_FIELD_NAMES = new Set([
  'latitude',
  'longitude',
  'lat',
  'lng',
  'lon',
]);

const getMaxDecimalsFromStep = (
  step?: React.InputHTMLAttributes<HTMLInputElement>['step']
): number => {
  if (step === undefined) {
    return 0;
  }

  if (step === 'any') {
    return 6;
  }

  const normalizedStep = String(step).replace(',', '.');
  const [, decimals = ''] = normalizedStep.split('.');
  return decimals.length;
};

const resolveAllowsNegative = (
  allowNegative: boolean | undefined,
  min?: React.InputHTMLAttributes<HTMLInputElement>['min']
): boolean => {
  if (allowNegative !== undefined) {
    return allowNegative;
  }

  if (min === undefined || min === '') {
    return false;
  }

  return Number(min) < 0;
};

const resolveDecimalSeparator = (
  decimalSeparator: 'comma' | 'dot' | undefined,
  name?: string,
  id?: string
): 'comma' | 'dot' => {
  if (decimalSeparator) {
    return decimalSeparator;
  }

  const normalizedName = name?.trim().toLowerCase();
  const normalizedId = id?.trim().toLowerCase();

  if (
    (normalizedName && COORDINATE_FIELD_NAMES.has(normalizedName)) ||
    (normalizedId &&
      (COORDINATE_FIELD_NAMES.has(normalizedId) ||
        Array.from(COORDINATE_FIELD_NAMES).some((fieldName) =>
          normalizedId.startsWith(`${fieldName}-`)
        )))
  ) {
    return 'dot';
  }

  return 'comma';
};

const NumberInput: React.FC<NumberInputProps> = ({
  value,
  onChange,
  onValueChange,
  className,
  inputClassName,
  allowNegative,
  maxDecimals,
  decimalSeparator,
  name,
  id,
  step,
  min,
  ...rest
}) => {
  const resolvedMaxDecimals = maxDecimals ?? getMaxDecimalsFromStep(step);
  const resolvedAllowNegative = resolveAllowsNegative(allowNegative, min);
  const resolvedDecimalSeparator = resolveDecimalSeparator(
    decimalSeparator,
    name,
    id
  );

  return (
    <PriceInput
      {...rest}
      id={id}
      name={name}
      step={step}
      min={min}
      value={value}
      allowNegative={resolvedAllowNegative}
      maxDecimals={resolvedMaxDecimals}
      decimalSeparator={resolvedDecimalSeparator}
      inputClassName={inputClassName ?? className}
      onChange={(nextValue) => {
        onValueChange?.(nextValue);

        if (!onChange) {
          return;
        }

        const nextStringValue =
          nextValue === undefined ? '' : String(nextValue);
        const syntheticEvent = {
          target: {
            name: name ?? '',
            id: id ?? '',
            value: nextStringValue,
            type: 'number',
          },
          currentTarget: {
            name: name ?? '',
            id: id ?? '',
            value: nextStringValue,
            type: 'number',
          },
        } as React.ChangeEvent<HTMLInputElement>;

        onChange(syntheticEvent);
      }}
    />
  );
};

export default NumberInput;
